/**
 * PropertyImage — single source of truth for property thumbnail rendering.
 *
 * Fallback ladder (per product spec, never renders a broken <img>):
 *   1. Real listing photo (property.image_url) if the URL is non-empty
 *      AND loads successfully.
 *   2. Mapbox Static Images API — satellite/aerial view centered on the
 *      property lat/lon. Badged "Aerial View" so users don't mistake it
 *      for a listing photograph. Communicates lot size, roof footprint,
 *      neighborhood density.
 *   3. Branded RealixData placeholder — SVG-based, no network dependency.
 *
 * Notes:
 *   - The Mapbox token comes from process.env.REACT_APP_MAPBOX_TOKEN and
 *     is reused from the existing Mapbox GL integration; no new signup.
 *   - We lazy-load images and keep the same 176px (h-44) aspect as before.
 *   - Falling back is stateful — once we drop to aerial, we don't retry
 *     the original URL for that mount.
 */
import { useMemo, useState } from "react";
import { MapPin, ZoomIn, ZoomOut, Images } from "lucide-react";
import { PhotoGallery } from "./PhotoGallery";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Static-Images defaults tuned for a "see the property + one row of
// neighbors" framing on a 400×176 card thumbnail.
const AERIAL_ZOOM_DEFAULT = 18;
const AERIAL_ZOOM_MIN = 14;   // Neighborhood context
const AERIAL_ZOOM_MAX = 19;   // Building-level
const AERIAL_WIDTH = 640;   // 2x for retina; card renders at ~320px wide
const AERIAL_HEIGHT = 280;

const buildMapboxAerialUrl = (lat, lon, zoom = AERIAL_ZOOM_DEFAULT) => {
  if (!MAPBOX_TOKEN || lat == null || lon == null) return null;
  // Subtle red pin so users can locate the property in the aerial.
  const marker = `pin-s+ef4444(${lon},${lat})`;
  return (
    `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/` +
    `${marker}/${lon},${lat},${zoom},0/` +
    `${AERIAL_WIDTH}x${AERIAL_HEIGHT}@2x?access_token=${MAPBOX_TOKEN}`
  );
};

const PlaceholderMark = ({ label = "No image available" }) => (
  <div
    data-testid="property-image-placeholder"
    className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 text-slate-400"
  >
    <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center mb-2">
      <MapPin className="w-5 h-5 text-emerald-500" />
    </div>
    <span className="text-[10px] uppercase tracking-widest font-semibold text-emerald-500/80">
      RealixData
    </span>
    <span className="text-[10px] text-slate-500 mt-0.5">{label}</span>
  </div>
);

export const PropertyImage = ({
  property,
  alt = "Property",
  className = "",
  imgClassName = "w-full h-44 object-cover",
  allowZoom = true,
}) => {
  // Track whether the primary image failed to load so we can fall through.
  const [stage, setStage] = useState(() => {
    const hasPhoto = !!(property?.image_url && String(property.image_url).trim());
    if (hasPhoto) return "photo";
    if (
      MAPBOX_TOKEN &&
      property?.latitude != null &&
      property?.longitude != null
    ) {
      return "aerial";
    }
    return "placeholder";
  });

  const [aerialZoom, setAerialZoom] = useState(AERIAL_ZOOM_DEFAULT);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const extraPhotos = Array.isArray(property?.photos) ? property.photos.filter(Boolean) : [];
  const totalPhotos = extraPhotos.length + (property?.image_url ? 1 : 0);
  const hasGallery = totalPhotos > 1;

  const openGallery = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setGalleryOpen(true);
  };

  const aerialUrl = useMemo(
    () => buildMapboxAerialUrl(property?.latitude, property?.longitude, aerialZoom),
    [property?.latitude, property?.longitude, aerialZoom]
  );

  const handlePhotoError = () => {
    if (aerialUrl) setStage("aerial");
    else setStage("placeholder");
  };

  const handleAerialError = () => setStage("placeholder");

  // Prevent card-level click handlers from firing when the user pinches
  // the zoom controls.
  const stopClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  const canZoomIn = aerialZoom < AERIAL_ZOOM_MAX;
  const canZoomOut = aerialZoom > AERIAL_ZOOM_MIN;

  return (
    <div className={`relative ${className}`} data-testid="property-image">
      {stage === "photo" && (
        <>
          <img
            src={property.image_url}
            alt={alt}
            className={imgClassName}
            loading="lazy"
            onError={handlePhotoError}
            data-testid="property-image-photo"
          />
          {hasGallery && (
            <button
              type="button"
              onClick={openGallery}
              className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-white text-[10px] font-semibold hover:bg-black/85 transition"
              data-testid="photo-gallery-open"
              aria-label={`View all ${totalPhotos} photos`}
            >
              <Images className="w-3 h-3" />
              +{totalPhotos - 1}
            </button>
          )}
        </>
      )}
      {stage === "aerial" && aerialUrl && (
        <>
          <img
            src={aerialUrl}
            alt={`${alt} — aerial view`}
            className={imgClassName}
            loading="lazy"
            onError={handleAerialError}
            data-testid="property-image-aerial"
          />
          <div
            className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-white text-[9px] font-medium uppercase tracking-wider"
            data-testid="aerial-view-badge"
          >
            <MapPin className="w-2.5 h-2.5" />
            Aerial View
          </div>
          {allowZoom && (
            <div
              className="absolute bottom-2 right-2 flex flex-col rounded-md overflow-hidden bg-black/70 backdrop-blur-sm shadow-md"
              onClick={stopClick}
              data-testid="aerial-zoom-controls"
            >
              <button
                type="button"
                aria-label="Zoom in"
                title="Zoom in"
                onClick={(e) => {
                  stopClick(e);
                  if (canZoomIn) setAerialZoom((z) => Math.min(AERIAL_ZOOM_MAX, z + 1));
                }}
                disabled={!canZoomIn}
                className={`p-1 text-white transition-opacity ${
                  canZoomIn ? "hover:bg-white/10" : "opacity-40 cursor-not-allowed"
                }`}
                data-testid="aerial-zoom-in"
              >
                <ZoomIn className="w-3 h-3" />
              </button>
              <div className="h-px bg-white/20" />
              <button
                type="button"
                aria-label="Zoom out"
                title="Zoom out"
                onClick={(e) => {
                  stopClick(e);
                  if (canZoomOut) setAerialZoom((z) => Math.max(AERIAL_ZOOM_MIN, z - 1));
                }}
                disabled={!canZoomOut}
                className={`p-1 text-white transition-opacity ${
                  canZoomOut ? "hover:bg-white/10" : "opacity-40 cursor-not-allowed"
                }`}
                data-testid="aerial-zoom-out"
              >
                <ZoomOut className="w-3 h-3" />
              </button>
            </div>
          )}
        </>
      )}
      {stage === "placeholder" && (
        <div className={imgClassName}>
          <PlaceholderMark
            label={
              property?.image_url
                ? "Preview unavailable"
                : "No image available"
            }
          />
        </div>
      )}
      {hasGallery && (
        <PhotoGallery
          photos={extraPhotos}
          coverImage={property?.image_url}
          address={property?.address || alt}
          isOpen={galleryOpen}
          onClose={() => setGalleryOpen(false)}
        />
      )}
    </div>
  );
};

export default PropertyImage;
